import React, { useState } from "react";
import {
  Grid,
  Typography,
  Button,
  Fade,
  CircularProgress,
} from "@material-ui/core";
import { withRouter } from "react-router-dom";

// styles
import useStyles from "../styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import TimerOffIcon from "@material-ui/icons/TimerOff";

// logo
import logo from "images/logoLoginRS.png";

// context
import { dbPublicGet, dbPublicPut } from "utils/DBFetchers.js";
import routes from "routes";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PasswordCreationFields, {
  isPasswordStrongEnough,
  passwordsMatch,
} from "components/CustomFields/PasswordCreationFields";

const safelyDecodeToken = (token) => {
  try {
    return decodeURIComponent(token);
  } catch (error) {
    return token;
  }
};

const getTokenFromSearch = (search) => {
  const tokenParam = (search || "")
    .replace(/^\?/, "")
    .split("&")
    .find((param) => param.split("=")[0] === "token");

  if (!tokenParam) return "";

  const token = tokenParam.split("=").slice(1).join("=");
  return safelyDecodeToken(token);
};

const getPasswordResetToken = (location) => {
  const tokenFromQuery = getTokenFromSearch(location.search);
  if (tokenFromQuery) return tokenFromQuery;

  const tokenFromPath = location.pathname.split(
    `${routes.passwordResetForm}/`,
  )[1];
  return tokenFromPath ? safelyDecodeToken(tokenFromPath) : "";
};

const getErrorMessage = (error, fallbackMessage) => {
  if (Array.isArray(error)) {
    return error[0]?.description_es || error[0]?.description || fallbackMessage;
  }

  return (
    error?.errors?.description_es?.[0] ||
    error?.errors?.description?.[0] ||
    error?.description_es ||
    error?.description ||
    error?.message ||
    fallbackMessage
  );
};

function PasswordResetForm(props) {
  let classes = useStyles();

  // local
  let [isTokenValid, setIsTokenValid] = useState(null);
  let [isLoading, setIsLoading] = useState(false);
  let [passwordValue1, setPasswordValue1] = useState("");
  let [passwordValue2, setPasswordValue2] = useState("");
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const [isFormValid, setIsFormValid] = useState({ valid: false, message: "" });
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const passwordMeetsCriteria = isPasswordStrongEnough(passwordValue1);
  const passwordsMatchConfirmation = passwordsMatch(
    passwordValue1,
    passwordValue2,
  );
  const checkFormValid = () => {
    if (!passwordMeetsCriteria) {
      setIsFormValid({
        valid: false,
        message: "La contraseña debe cumplir todos los criterios de seguridad",
      });
      return false;
    } else if (!passwordsMatchConfirmation) {
      setIsFormValid({
        valid: false,
        message: "Las contraseñas deben coincidir",
      });
      return false;
    }
    setIsFormValid({
      valid: true,
      message: "",
    });
    return true;
  };

  const handlePageToken = () => {
    const token = getPasswordResetToken(props.location);
    if (!token) {
      setIsTokenValid(false);
      return;
    }

    dbPublicGet(`update-token-check?token=${encodeURIComponent(token)}`)
      .then((data) => {
        setIsTokenValid(true);
      })
      .catch((error) => {
        setIsTokenValid(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!checkFormValid()) {
      return;
    }
    const token = getPasswordResetToken(props.location);
    if (!token || isLoading || isPasswordUpdated) {
      return;
    }

    setIsLoading(true);
    let passwordData = {
      password: passwordValue1,
    };
    dbPublicPut(
      `users/reset-password?token=${encodeURIComponent(token)}`,
      passwordData,
    )
      .then((data) => {
        setIsLoading(false);
        setIsPasswordUpdated(true);
        setPasswordValue1("");
        setPasswordValue2("");
        snackbarOpen("success", "Contraseña actualizada exitosamente");
      })
      .catch((error) => {
        setIsLoading(false);
        snackbarOpen(
          "error",
          getErrorMessage(error, "Error al editar contraseña"),
        );
        return;
      });
  };

  const handleGoLogin = (e) => {
    e.preventDefault();
    props.history.push(routes.login);
  };

  /* On page load, get all data */
  React.useEffect(() => {
    handlePageToken();
  }, []);

  return (
    <Grid container className={classes.container}>
      <div className={classes.logotypeContainer}>
        <img src={logo} alt="logo" className={classes.logotypeImage} />
      </div>
      <div className={classes.formContainer}>
        <div className={classes.form}>
          <CustomizedSnackbars
            severity={snackbarStatus.severity}
            mssg={snackbarStatus.message}
            open={snackbarStatus.open}
            onClose={onCloseSnackbar}
          />
          {isTokenValid ? (
            <>
              {isPasswordUpdated ? (
                <div align="center">
                  <CheckCircleOutlineIcon
                    fontSize="large"
                    color="primary"
                    style={{ marginBottom: 20 }}
                  />
                  <Typography
                    variant="h4"
                    align="center"
                    style={{ marginBottom: 24, fontWeight: "bold" }}
                  >
                    Contraseña actualizada exitosamente
                  </Typography>
                  <Typography variant="h6" align="center">
                    Ya puedes iniciar sesión con tu nueva contraseña.
                  </Typography>
                  <Button
                    onClick={handleGoLogin}
                    type="button"
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.backButtonsPassReset}
                  >
                    Volver al login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div>
                    <Typography
                      variant="h3"
                      align="center"
                      style={{ fontWeight: "bold" }}
                    >
                      Nueva contraseña
                    </Typography>{" "}
                  </div>
                  <div className={classes.formDividerContainer}>
                    <div className={classes.formDivider} />
                  </div>
                  <Typography variant="h6" align="center">
                    Ingresa una nueva contraseña
                  </Typography>
                  <Fade in={!isFormValid.valid}>
                    <Typography
                      color="secondary"
                      className={classes.errorMessage}
                    >
                      {isFormValid.message}
                    </Typography>
                  </Fade>
                  <PasswordCreationFields
                    password={passwordValue1}
                    confirmation={passwordValue2}
                    onPasswordChange={(event) =>
                      setPasswordValue1(event.target.value)
                    }
                    onConfirmationChange={(event) =>
                      setPasswordValue2(event.target.value)
                    }
                  />
                  <div className={classes.formButtonsPassReset}>
                    {isLoading ? (
                      <CircularProgress
                        size={26}
                        className={classes.loginLoader}
                        style={{ marginLeft: "0%" }}
                      />
                    ) : (
                      <Button
                        disabled={
                          !passwordMeetsCriteria || !passwordsMatchConfirmation
                        }
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                      >
                        Cambiar contraseña
                      </Button>
                    )}
                  </div>
                </form>
              )}
            </>
          ) : isTokenValid === false ? (
            <div align="center">
              <TimerOffIcon
                fontSize="large"
                style={{ marginBottom: 20 }}
              ></TimerOffIcon>
              <Typography
                variant="h4"
                align="center"
                style={{ marginBottom: 100 }}
              >
                El link de recuperación ha expirado
              </Typography>
              <Button
                onClick={handleGoLogin}
                type="button"
                variant="contained"
                color="primary"
                size="large"
                className={classes.backButtonsPassReset}
              >
                Volver
              </Button>
            </div>
          ) : (
            <CircularProgress
              size={26}
              className={classes.loginLoader}
              style={{ marginLeft: "45%" }}
            />
          )}
        </div>
        <Typography color="primary" className={classes.copyright}>
          By Dynaxo Corp.
        </Typography>
      </div>
    </Grid>
  );
}

export default withRouter(PasswordResetForm);
